import { useTranslation } from "react-i18next";
import { Play, CreditCard } from "lucide-react";
import { ConfirmDialog, Alert, AlertDescription } from "@tellescope/ui";
import { formatCurrency, formatCardBrand } from "@tellescope/utils";
import { getStripePlanProductName } from "../../../hooks/useStripeSubscriptions";
import type { StripeSubscription } from "../../../hooks/useStripeSubscriptions";
import { useStripeSubscriptionMutation } from "../../../hooks/useStripeSubscriptionMutations";

interface ResumeConfirmDialogProps {
  subscription: StripeSubscription;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function ResumeConfirmDialog({ subscription, open, onOpenChange, onSuccess }: ResumeConfirmDialogProps) {
  const { t } = useTranslation("payments");
  const resumeMutation = useStripeSubscriptionMutation();

  const { effectivePlan } = subscription;
  const productName = getStripePlanProductName(effectivePlan);
  const price = effectivePlan
    ? formatCurrency(effectivePlan.amount, effectivePlan.currency, { fromSmallestUnit: true })
    : null;
  const interval = effectivePlan?.interval;
  const cardInfo = subscription.default_payment_method?.card;

  const handleConfirm = () => {
    resumeMutation.mutate(
      {
        integration: "Stripe",
        type: "subscription-resume",
        id: subscription.id,
      },
      { onSuccess }
    );
  };

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      variant="default"
      icon={<Play className="h-5 w-5" />}
      title={t("Resume Subscription")}
      description={
        productName ? t("Ready to resume {{productName}}?", { productName }) : t("Ready to resume your subscription?")
      }
      confirmText={price ? t("Resume & Pay {{price}}", { price }) : t("Resume")}
      cancelText={t("Stay Paused")}
      onConfirm={handleConfirm}
      loading={resumeMutation.isPending}
      customContent={
        <div className="mt-2 space-y-3">
          {/* Subscription details */}
          <div className="p-3 bg-muted rounded-md text-sm space-y-1">
            {price !== null && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t("Amount")}</span>
                <span className="font-medium">
                  {price}/{interval}
                </span>
              </div>
            )}
            {cardInfo && (
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">{t("Payment method")}</span>
                <span className="flex items-center gap-1">
                  <CreditCard className="h-3 w-3" />
                  {formatCardBrand(cardInfo.brand)} ****{cardInfo.last4}
                </span>
              </div>
            )}
          </div>

          {/* Warning if there might be past due amount */}
          {subscription.status === "past_due" && (
            <Alert variant="destructive">
              <AlertDescription>
                {t("You may have an outstanding balance that will be charged when you resume.")}
              </AlertDescription>
            </Alert>
          )}
        </div>
      }
    />
  );
}
